import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    MenuItem,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    CircularProgress,
    Autocomplete
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { purchaseOrdersAPI, customersAPI } from '../api/api';
import { API_BASE_URL, formatCurrency, formatDateForInput } from '../config';

const POForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState(null);

    const [formData, setFormData] = useState({
        po_date: new Date().toISOString().split('T')[0],
        expected_delivery: '',
        status: 'DRAFT',
        notes: ''
    });

    const [items, setItems] = useState([
        { description: '', quantity: 1, unit: 'KG', unit_price: 0 }
    ]);

    useEffect(() => {
        loadInitialData();
    }, [id]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            // Fetch Vendors
            const vendorsRes = await fetch(`${API_BASE_URL}/vendors/active`);
            const vendorsData = await vendorsRes.json();

            if (vendorsData.success) {
                setVendors(vendorsData.data);
            }

            if (id) {
                const poRes = await purchaseOrdersAPI.getById(id);
                if (poRes.data.success) {
                    const po = poRes.data.data;
                    setFormData({
                        po_date: formatDateForInput(po.po_date),
                        expected_delivery: formatDateForInput(po.expected_delivery) || '',
                        status: po.status,
                        notes: po.notes || ''
                    });

                    // Logic to handle existing POs
                    if (po.vendor_id) {
                        setSelectedVendor(vendorsData.data.find(v => v.id === po.vendor_id));
                    }

                    setItems(po.items.length > 0 ? po.items : [{ description: '', quantity: 1, unit: 'KG', unit_price: 0 }]);
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { description: '', quantity: 1, unit: 'KG', unit_price: 0 }]);
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
        }
    };

    const calculateTotal = () => {
        return items.reduce((sum, item) => {
            return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
        }, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedVendor) {
            toast.error('Please select a vendor');
            return;
        }

        if (items.some(item => !item.description || !item.unit_price)) {
            toast.error('Please fill in all item details');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                ...formData,
                vendor_id: selectedVendor.id,
                items: items.map(item => ({
                    ...item,
                    quantity: parseFloat(item.quantity) || 1,
                    unit_price: parseFloat(item.unit_price) || 0
                }))
            };

            if (isEdit) {
                await purchaseOrdersAPI.update(id, payload);
                toast.success('PO updated successfully');
            } else {
                await purchaseOrdersAPI.create(payload);
                toast.success('PO created successfully');
            }

            navigate('/purchase-orders');
        } catch (error) {
            console.error('Error saving PO:', error);
            toast.error('Failed to save PO');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/purchase-orders')}>
                    Back
                </Button>
                <Typography variant="h5" fontWeight={600}>
                    {isEdit ? 'Edit Purchase Order' : 'New Purchase Order'}
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* PO Details */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>PO Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={vendors}
                                            getOptionLabel={(option) => option.name}
                                            value={selectedVendor}
                                            onChange={(e, value) => setSelectedVendor(value)}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Vendor" required />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            label="PO Date"
                                            type="date"
                                            value={formData.po_date}
                                            onChange={(e) => setFormData({ ...formData, po_date: e.target.value })}
                                            fullWidth
                                            required
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            label="Expected Delivery"
                                            type="date"
                                            value={formData.expected_delivery}
                                            onChange={(e) => setFormData({ ...formData, expected_delivery: e.target.value })}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Status"
                                            select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            fullWidth
                                        >
                                            <MenuItem value="DRAFT">Draft</MenuItem>
                                            <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                                            <MenuItem value="FULFILLED">Fulfilled</MenuItem>
                                            <MenuItem value="CANCELLED">Cancelled</MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Items */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">Items</Typography>
                                    <Button startIcon={<AddIcon />} onClick={addItem} size="small">
                                        Add Item
                                    </Button>
                                </Box>

                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ minWidth: 250 }}>Description</TableCell>
                                                <TableCell sx={{ width: 100 }}>Qty</TableCell>
                                                <TableCell sx={{ width: 100 }}>Unit</TableCell>
                                                <TableCell sx={{ width: 120 }}>Price</TableCell>
                                                <TableCell sx={{ width: 120 }} align="right">Amount</TableCell>
                                                <TableCell sx={{ width: 50 }}></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {items.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <TextField
                                                            value={item.description}
                                                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            placeholder="Item description"
                                                            required
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            inputProps={{ min: 0, step: 0.001 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            select
                                                            value={item.unit}
                                                            onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                        >
                                                            <MenuItem value="KG">KG</MenuItem>
                                                            <MenuItem value="Bag">Bag</MenuItem>
                                                            <MenuItem value="Nos">Nos</MenuItem>
                                                            <MenuItem value="Ltr">Ltr</MenuItem>
                                                        </TextField>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            value={item.unit_price}
                                                            onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            inputProps={{ min: 0, step: 0.01 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => removeItem(index)}
                                                            disabled={items.length === 1}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>

                        {/* Notes */}
                        <Card>
                            <CardContent>
                                <TextField
                                    label="Notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Summary */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ position: 'sticky', top: 16 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Summary</Typography>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                    <Typography variant="h6">Total</Typography>
                                    <Typography variant="h6" color="secondary" fontWeight={700}>
                                        {formatCurrency(calculateTotal())}
                                    </Typography>
                                </Box>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    disabled={saving}
                                >
                                    {isEdit ? 'Update PO' : 'Create PO'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default POForm;
